// @flow
import { ResourceDocumentContext } from 'components/ResourceFill/providers/ResourceDocumentProvider'
import { ResourceFormContext } from 'components/ResourceFill/providers/ResourceFormProvider'
import { ResourceResponseContext } from 'components/ResourceFill/providers/ResourceResponseProvider'
import { ResourceSessionsContext } from 'components/ResourceFill/providers/ResourceSessionsProvider'
import { ResourceSharedContext } from 'components/ResourceFill/providers/ResourceSharedProvider'
import type { SaveContext } from 'components/ResourceFill/types/save-context'
import * as React from 'react'
import type { Session } from 'types/session'

type Props = {
  children: React.Node,
}

export const ResourceSaveContext: React.Context<?SaveContext> =
  React.createContext(null)

function ResourceSaveProvider({ children }: Props): React.Node {
  const [saving, setSaving] = React.useState(false)

  const { response } = React.useContext(ResourceResponseContext) || {}

  const { saveDocument } = React.useContext(ResourceDocumentContext) || {}

  const { saveForm } = React.useContext(ResourceFormContext) || {}

  const { mode } = React.useContext(ResourceSharedContext) || {}

  const { setSessions, currentSession } =
    React.useContext(ResourceSessionsContext) || {}

  const saveInternalForm = React.useCallback(() => {
    if (!setSessions) return
    if (!currentSession) return

    setSessions({
      [currentSession.id]: {
        ...currentSession,
        isSubmitted: true,
        savedForLater: true,
      },
    })
  }, [currentSession, setSessions])

  const save = React.useCallback(
    async ({
      validate = true,
      submit = false,
      lockInternalForm = false,
    }: {
      validate: boolean,
      submit?: boolean,
      lockInternalForm?: boolean,
    }): Promise<?Session> => {
      if (saving) return
      if (!saveDocument) return
      if (!saveForm) return

      setSaving(true)

      if (response?.document) {
        const result = await saveDocument(validate, submit, lockInternalForm)
        setSaving(false)
        return result
      }

      const result = await saveForm(validate, submit, lockInternalForm)
      setSaving(false)

      if (result && mode === 'internal') {
        return saveInternalForm() // it's important to return 'undefined' here
      }

      return result
    },
    [saving, saveDocument, saveForm, response?.document, mode, saveInternalForm]
  )

  const value = { save, saving }

  return (
    <ResourceSaveContext.Provider value={value}>
      {children}
    </ResourceSaveContext.Provider>
  )
}

export default ResourceSaveProvider
